import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pagination, Checkbox, Empty } from "antd";

import {
  InterestAction,
  PhotoreqAction,
  SearchAction,
} from "../../store/actions";

import useScreenSize from "../../utils/usescreensize";
import RemoveEmptyKeys from "../../utils/removeemptykeys";
import {
  getArrayParam,
  getNumberParam,
  getStringParam,
  setArrayParam,
  setNumberParam,
  setStringParam,
} from "../../utils/urlsearchparams";

import EditProfileDropdown from "../../components/dropdown/editprofiledropdown";
import PrimaryButton from "../../components/button/primarybutton";
import UnderHeaderMenu from "../../components/underheadermenu";
import Spacer from "../../components/spacer/spacer";
import Cardloader from "../../components/loader/cardloader";
import DashboardCard from "../../components/dashboardcard/dashboardcard";
import CardBottomButton from "../../components/dashboardcard/cardbottombutton";

import BasicInfoFrom from "./basicinfofrom";
import AppeLifeStyleFrom from "./appelifestylefrom";
import FamilyDetailsFrom from "./familydetailsfrom";
import ReligiousStatusFrom from "./religiousstatusfrom";
import EducationCareerForm from "./educationcareerform";

import images from "../../assets";
import "./styles.css";

const AdvancedSearch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const screenSize = useScreenSize();
  const [searchParams, setSearchParams] = useSearchParams();

  const { advancedMatches, advancedCount, isAdvancedLoading } = useSelector(
    (state) => state.search
  );
  const gender = useSelector((state) => state.profile?.profile?.gender);
  const { interestsAll } = useSelector((state) => state.interests);
  const { photoreqAll } = useSelector((state) => state.photoreq);

  const pageNo = getNumberParam(searchParams, "page", 1);
  const pageSize = getNumberParam(searchParams, "size", 8);
  const sortParam = getStringParam(searchParams, "sort", "desc");
  const searched = searchParams.get("searched") === "1";
  const filterParamsKey = [
    "country",
    "ageMin",
    "ageMax",
    "spokenLanguage",
    "ethnicOrigin",
    "haveChildren",
    "stateProvince",
    "residencyStatus",
    "maritalStatus",
    "relocationPlan",
    "profileCreatedBy",
    "typeOfProfile",
    "hasPhoto",
    "hairColor",
    "heightMin",
    "heightMax",
    "bodyType",
    "bloodGroup",
    "foodHabit",
    "financialStatus",
    "familyType",
    "religiousHistory",
    "beard",
    "hijab",
    "readQuran",
    "religiousSect",
    "dailyPrayer",
    "religiousCommitment",
    "polygamy",
    "education",
    "profession",
    "incomeCurrency",
    "annualIncomeMin",
    "annualIncomeMax",
  ]
    .map((key) => searchParams.getAll(key).join(","))
    .join("|");

  const [sortOption, setSortOption] = useState(sortParam);
  const [matchedUsers, setMatchedUsers] = useState([]);
  const [incomeError, setIncomeError] = useState("");

  const [state, setState] = useState({
    country: [],
    ageMin: 18,
    ageMax: 30,
    spokenLanguage: [""],
    ethnicOrigin: [""],
    haveChildren: [""],
    stateProvince: [],
    residencyStatus: [""],
    maritalStatus: [""],
    relocationPlan: [""],
    profileCreatedBy: [""],
    typeOfProfile: [""],
    hasPhoto: [""],
    hairColor: [""],
    heightMin: 142,
    heightMax: 158,
    bodyType: [""],
    bloodGroup: [""],
    foodHabit: [""],
    financialStatus: [""],
    familyType: [""],
    religiousHistory: [""],
    beard: [""],
    hijab: [""],
    readQuran: [""],
    religiousSect: [""],
    dailyPrayer: [""],
    religiousCommitment: [""],
    polygamy: [""],
    education: [""],
    profession: [""],
    incomeCurrency: "",
    annualIncomeMin: "",
    annualIncomeMax: "",
  });

  useEffect(() => {
    setMatchedUsers(advancedMatches);
  }, [advancedMatches]);

  useEffect(() => {
    setSortOption(sortParam);
  }, [sortParam]);

  useEffect(() => {
    const getArrayWithDefault = (key, fallback) =>
      searchParams.has(key)
        ? getArrayParam(searchParams, key, fallback)
        : fallback;

    setState({
      country: getArrayParam(searchParams, "country", []),
      ageMin: getNumberParam(searchParams, "ageMin", 18),
      ageMax: getNumberParam(searchParams, "ageMax", 30),
      spokenLanguage: getArrayWithDefault("spokenLanguage", [""]),
      ethnicOrigin: getArrayWithDefault("ethnicOrigin", [""]),
      haveChildren: getArrayWithDefault("haveChildren", [""]),
      stateProvince: getArrayParam(searchParams, "stateProvince", []),
      residencyStatus: getArrayWithDefault("residencyStatus", [""]),
      maritalStatus: getArrayWithDefault("maritalStatus", [""]),
      relocationPlan: getArrayWithDefault("relocationPlan", [""]),
      profileCreatedBy: getArrayWithDefault("profileCreatedBy", [""]),
      typeOfProfile: getArrayWithDefault("typeOfProfile", [""]),
      hasPhoto: getArrayWithDefault("hasPhoto", [""]),
      hairColor: getArrayWithDefault("hairColor", [""]),
      heightMin: getNumberParam(searchParams, "heightMin", 142),
      heightMax: getNumberParam(searchParams, "heightMax", 158),
      bodyType: getArrayWithDefault("bodyType", [""]),
      bloodGroup: getArrayWithDefault("bloodGroup", [""]),
      foodHabit: getArrayWithDefault("foodHabit", [""]),
      financialStatus: getArrayWithDefault("financialStatus", [""]),
      familyType: getArrayWithDefault("familyType", [""]),
      religiousHistory: getArrayWithDefault("religiousHistory", [""]),
      beard: getArrayWithDefault("beard", [""]),
      hijab: getArrayWithDefault("hijab", [""]),
      readQuran: getArrayWithDefault("readQuran", [""]),
      religiousSect: getArrayWithDefault("religiousSect", [""]),
      dailyPrayer: getArrayWithDefault("dailyPrayer", [""]),
      religiousCommitment: getArrayWithDefault("religiousCommitment", [""]),
      polygamy: getArrayWithDefault("polygamy", [""]),
      education: getArrayWithDefault("education", [""]),
      profession: getArrayWithDefault("profession", [""]),
      incomeCurrency: getStringParam(searchParams, "incomeCurrency", ""),
      annualIncomeMin: getStringParam(searchParams, "annualIncomeMin", ""),
      annualIncomeMax: getStringParam(searchParams, "annualIncomeMax", ""),
    });
  }, [filterParamsKey]);

  useEffect(() => {
    if (!searched) return;

    const searchData = RemoveEmptyKeys({
      country: getArrayParam(searchParams, "country", []),
      ageMin: getNumberParam(searchParams, "ageMin", 18),
      ageMax: getNumberParam(searchParams, "ageMax", 30),
      spokenLanguage: getArrayParam(searchParams, "spokenLanguage", []),
      ethnicOrigin: getArrayParam(searchParams, "ethnicOrigin", []),
      haveChildren: getArrayParam(searchParams, "haveChildren", []),
      stateProvince: getArrayParam(searchParams, "stateProvince", []),
      residencyStatus: getArrayParam(searchParams, "residencyStatus", []),
      maritalStatus: getArrayParam(searchParams, "maritalStatus", []),
      relocationPlan: getArrayParam(searchParams, "relocationPlan", []),
      profileCreatedBy: getArrayParam(searchParams, "profileCreatedBy", []),
      typeOfProfile: getArrayParam(searchParams, "typeOfProfile", []),
      hasPhoto: getArrayParam(searchParams, "hasPhoto", []),
      hairColor: getArrayParam(searchParams, "hairColor", []),
      heightMin: getNumberParam(searchParams, "heightMin", 142),
      heightMax: getNumberParam(searchParams, "heightMax", 158),
      bodyType: getArrayParam(searchParams, "bodyType", []),
      bloodGroup: getArrayParam(searchParams, "bloodGroup", []),
      foodHabit: getArrayParam(searchParams, "foodHabit", []),
      financialStatus: getArrayParam(searchParams, "financialStatus", []),
      familyType: getArrayParam(searchParams, "familyType", []),
      religiousHistory: getArrayParam(searchParams, "religiousHistory", []),
      beard: getArrayParam(searchParams, "beard", []),
      hijab: getArrayParam(searchParams, "hijab", []),
      readQuran: getArrayParam(searchParams, "readQuran", []),
      religiousSect: getArrayParam(searchParams, "religiousSect", []),
      dailyPrayer: getArrayParam(searchParams, "dailyPrayer", []),
      religiousCommitment: getArrayParam(searchParams, "religiousCommitment", []),
      polygamy: getArrayParam(searchParams, "polygamy", []),
      education: getArrayParam(searchParams, "education", []),
      profession: getArrayParam(searchParams, "profession", []),
      incomeCurrency: getStringParam(searchParams, "incomeCurrency", ""),
      annualIncomeMin: getStringParam(searchParams, "annualIncomeMin", ""),
      annualIncomeMax: getStringParam(searchParams, "annualIncomeMax", ""),
    });

    const payload = {
      searchData,
      from: (pageNo - 1) * pageSize,
      size: pageSize,
      gender,
      sort: sortParam,
    };
    dispatch(SearchAction.advancedSearch(payload));
  }, [
    dispatch,
    gender,
    pageNo,
    pageSize,
    filterParamsKey,
    searched,
    sortParam,
  ]);

  const handleSendInterest = (id) => {
    dispatch(InterestAction.sendInterest({ receiverId: id }));
  };

  const handleSendPhotoReq = (id) => {
    dispatch(PhotoreqAction.sendPhotoreq({ receiverId: id }));
  };

  const handleSearch = () => {
    if (
      (state?.annualIncomeMax || state?.annualIncomeMin) &&
      state?.annualIncomeMax <= state?.annualIncomeMin
    ) {
      setIncomeError("Max income must be greater than minimum income");
    } else {
      setIncomeError("");
      const removeEmptyKeys = RemoveEmptyKeys(state);
      const params = new URLSearchParams(searchParams);
      setNumberParam(params, "ageMin", removeEmptyKeys.ageMin);
      setNumberParam(params, "ageMax", removeEmptyKeys.ageMax);
      setArrayParam(params, "country", removeEmptyKeys.country);
      setArrayParam(params, "stateProvince", removeEmptyKeys.stateProvince);
      setArrayParam(params, "spokenLanguage", removeEmptyKeys.spokenLanguage);
      setArrayParam(params, "ethnicOrigin", removeEmptyKeys.ethnicOrigin);
      setArrayParam(params, "haveChildren", removeEmptyKeys.haveChildren);
      setArrayParam(params, "residencyStatus", removeEmptyKeys.residencyStatus);
      setArrayParam(params, "maritalStatus", removeEmptyKeys.maritalStatus);
      setArrayParam(params, "relocationPlan", removeEmptyKeys.relocationPlan);
      setArrayParam(
        params,
        "profileCreatedBy",
        removeEmptyKeys.profileCreatedBy
      );
      setArrayParam(params, "typeOfProfile", removeEmptyKeys.typeOfProfile);
      setArrayParam(params, "hasPhoto", removeEmptyKeys.hasPhoto);
      setArrayParam(params, "hairColor", removeEmptyKeys.hairColor);
      setNumberParam(params, "heightMin", removeEmptyKeys.heightMin);
      setNumberParam(params, "heightMax", removeEmptyKeys.heightMax);
      setArrayParam(params, "bodyType", removeEmptyKeys.bodyType);
      setArrayParam(params, "bloodGroup", removeEmptyKeys.bloodGroup);
      setArrayParam(params, "foodHabit", removeEmptyKeys.foodHabit);
      setArrayParam(
        params,
        "financialStatus",
        removeEmptyKeys.financialStatus
      );
      setArrayParam(params, "familyType", removeEmptyKeys.familyType);
      setArrayParam(
        params,
        "religiousHistory",
        removeEmptyKeys.religiousHistory
      );
      setArrayParam(params, "beard", removeEmptyKeys.beard);
      setArrayParam(params, "hijab", removeEmptyKeys.hijab);
      setArrayParam(params, "readQuran", removeEmptyKeys.readQuran);
      setArrayParam(params, "religiousSect", removeEmptyKeys.religiousSect);
      setArrayParam(params, "dailyPrayer", removeEmptyKeys.dailyPrayer);
      setArrayParam(
        params,
        "religiousCommitment",
        removeEmptyKeys.religiousCommitment
      );
      setArrayParam(params, "polygamy", removeEmptyKeys.polygamy);
      setArrayParam(params, "education", removeEmptyKeys.education);
      setArrayParam(params, "profession", removeEmptyKeys.profession);
      setStringParam(params, "incomeCurrency", removeEmptyKeys.incomeCurrency);
      setStringParam(params, "annualIncomeMin", removeEmptyKeys.annualIncomeMin);
      setStringParam(params, "annualIncomeMax", removeEmptyKeys.annualIncomeMax);
      setStringParam(params, "sort", sortOption);
      setStringParam(params, "searched", "1");
      setNumberParam(params, "page", 1);
      setSearchParams(params);
    }
  };

  const handlePageChange = (pageNumber) => {
    const params = new URLSearchParams(searchParams);
    setNumberParam(params, "page", pageNumber);
    setSearchParams(params);
  };

  const handleSortChange = (value) => {
    setSortOption(value);
    const params = new URLSearchParams(searchParams);
    setStringParam(params, "sort", value);
    setNumberParam(params, "page", 1);
    setSearchParams(params);
  };

  const handleClear = () => {
    const params = new URLSearchParams(searchParams);
    [
      "country",
      "ageMin",
      "ageMax",
      "spokenLanguage",
      "ethnicOrigin",
      "haveChildren",
      "stateProvince",
      "residencyStatus",
      "maritalStatus",
      "relocationPlan",
      "profileCreatedBy",
      "typeOfProfile",
      "hasPhoto",
      "hairColor",
      "heightMin",
      "heightMax",
      "bodyType",
      "bloodGroup",
      "foodHabit",
      "financialStatus",
      "familyType",
      "religiousHistory",
      "beard",
      "hijab",
      "readQuran",
      "religiousSect",
      "dailyPrayer",
      "religiousCommitment",
      "polygamy",
      "education",
      "profession",
      "incomeCurrency",
      "annualIncomeMin",
      "annualIncomeMax",
      "searched",
      "page",
    ].forEach((key) => params.delete(key));
    setSearchParams(params);
  };

  return (
    <div className="adv-search-recommended-container">
      {!searched ? (
        <>
          {screenSize.width > 1023 ? (
            <div className="adv-search-header-container">
              <h1 className="section-heading">Advanced Search</h1>
              <button className="advance-clear-btn" onClick={handleClear}>
                Clear Form
              </button>
            </div>
          ) : (
            <UnderHeaderMenu titlename="Advanced Search">
              <p onClick={handleClear}>Clear</p>
            </UnderHeaderMenu>
          )}
          <BasicInfoFrom state={state} setState={setState} />
          <AppeLifeStyleFrom state={state} setState={setState} />
          <FamilyDetailsFrom state={state} setState={setState} />
          <ReligiousStatusFrom state={state} setState={setState} />
          <EducationCareerForm
            state={state}
            setState={setState}
            incomeError={incomeError}
          />
          <div className="adv-search-btn" style={{ padding: "0px 24px" }}>
            <Checkbox>Save search preferences</Checkbox>
          </div>
          <Spacer height="16px" />
          <div className="flex justify-center px-4">
            <PrimaryButton
              text="Search"
              style={{ width: "518px" }}
              onClick={handleSearch}
            />
          </div>
          <Spacer height="58px" />
        </>
      ) : (
        <div className="search-results-container">
          <PrimaryButton
            text={"Back"}
            icon={images.arrowIconLeft}
            iconalign="left"
            style={{ width: "120px" }}
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.delete("searched");
              params.delete("page");
              setSearchParams(params);
            }}
          />
          <Spacer height="10px" />
          <div className="header-container">
            <h1 className="section-heading">Search Results</h1>
            <div style={{ width: "150px" }}>
              <EditProfileDropdown
                data={[
                  { label: "Newest First", value: "desc" },
                  { label: "Oldest First", value: "asc" },
                ]}
                onChange={handleSortChange}
                value={sortOption}
                placeholder="Sort by"
              />
            </div>
          </div>
          {isAdvancedLoading ? (
            <Cardloader />
          ) : advancedMatches?.length > 0 ? (
            <>
              <div className="recommended-matches-container">
                {matchedUsers?.map((item, index) => {
                  const interestSent = interestsAll?.some(
                    (profile) => profile.receiverId === item?.username
                  );
                  const photoreqSent = photoreqAll?.some(
                    (profile) => profile.receiverId === item?.username
                  );
                  return (
                    <DashboardCard
                      item={item}
                      index={index}
                      key={index}
                      redirectedfrom="advancedSearch"
                    >
                      {item?.matchOfTheDay && (
                        <CardBottomButton icon={images.cardButtonTick} />
                      )}
                      <CardBottomButton
                        icon={images.cardMessage}
                        onClick={() =>
                          navigate(
                            `/messages?username=${item?.username}&firstName=${item?.firstName}&lastName=${item?.lastName}&photo=${item?.profilePicture}`
                          )
                        }
                        title="Send Message"
                      />
                      <CardBottomButton
                        icon={images.cardPhotoReq}
                        onClick={() => handleSendPhotoReq(item?.username)}
                        title="Send Photo Request"
                        disabled={photoreqSent}
                      />
                      <CardBottomButton
                        icon={images.cardmatch}
                        onClick={() => handleSendInterest(item?.username)}
                        title="Send Interest"
                        disabled={interestSent}
                      />
                    </DashboardCard>
                  );
                })}
              </div>
              <Spacer height="36px" />
              <div className="flex justify-center items-center">
                <Pagination
                  current={pageNo}
                  total={advancedCount}
                  defaultPageSize={pageSize}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                />
              </div>
              <Spacer height="24px" />
            </>
          ) : (
            <Empty description={<p>No Results found</p>} />
          )}
        </div>
      )}
    </div>
  );
};

export default AdvancedSearch;
